// src/routes/index.tsx

/* --- Root-Seiten (Workspaces) --- */

import { createBrowserRouter, Outlet } from "react-router-dom";

import { NotificationsApp } from "@/domains/admin/notifications";
import { RoadmapApp } from "@/domains/admin/roadmap";
import { AnalyticsScreen } from "@/domains/admin/roadmap/screen/Analytics/AnalyticsScreen";
import { KanbanScreen } from "@/domains/admin/roadmap/screen/Kanban/KanbanScreen";
import { MatrixScreen } from "@/domains/admin/roadmap/screen/Matrix/MatrixScreen";
import { OverviewScreen } from "@/domains/admin/roadmap/screen/Overview/OverviewScreen";
import { TimelineScreen } from "@/domains/admin/roadmap/screen/Timeline/TimelineScreen";
import { DashboardApp } from "@/domains/core/dashboard";
import { AppLayout } from "@/domains/core/layout";
import { ErrorPage } from "@/domains/core/screen";
import { HealthApp } from "@/domains/health/HealthApp";
import {
  OuraApp,
  ReadinessDetail,
  SleepDetail,
  ActivityDetail,
  StressDetail,
} from "@/domains/health/oura";
import { OuraDashboardPage } from "@/domains/health/oura/components/pages/OuraDashboardPage";
import { WithingsApp } from "@/domains/health/withings";
import {
  WithingsOverviewPage,
  WithingsBodyCompositionPage,
  WithingsTrendsPage,
  WithingsSyncPage,
  WithingsTokenPage,
} from "@/domains/health/withings/components/pages";
import { YazioApp } from "@/domains/health/yazio";
import {
  YazioDashboardPage,
  YazioGoalsPage,
  YazioMealsPage,
  YazioSyncPage,
} from "@/domains/health/yazio/components/pages";
import { ParqetApp } from "@/domains/investments";
/* --- Settings & weitere Tools --- */
import { ParqetSettingsView } from "@/domains/investments/parqet/screen/ParqetSettingsView";
import { LLMApp, ChatGPTApp, OllamaApp } from "@/domains/llm";
import { CalendarPage } from "@/domains/productivity/calendar";
import { MiracleFlowApp } from "@/domains/productivity/miracle-flow";
import {
  MiracleFlowDashboardTab,
  MiracleFlowSessionHistoryTab,
  MiracleFlowAffirmationSettingsTab,
  MiracleFlowStatsOverviewTab,
} from "@/domains/productivity/miracle-flow/pages";
import { ModernMiracleFlowEveningSessionTab } from "@/domains/productivity/miracle-flow/pages/ModernMiracleFlowEveningSessionTab";
import { ModernMiracleFlowMorningSessionTab } from "@/domains/productivity/miracle-flow/pages/ModernMiracleFlowMorningSessionTab";
import { PersonalDevelopmentApp } from "@/domains/productivity/PersonalDevelopmentApp";
import { RemindersApp } from "@/domains/productivity/reminders";
import { SpotifyPage } from "@/domains/productivity/spotify";
/* --- Einzelseiten aus den Unter­kategorien --- */
import { TimeTracQApp } from "@/domains/productivity/timetracq";
import {
  TrackingPage,
  TasksPage,
  DailyPlanPage,
  ShortfallsPage,
  OverviewPage,
  AggregatedViewPage,
  BuzzerPage,
} from "@/domains/productivity/timetracq/components/pages";
import { PersonalInformationPage } from "@/domains/settings/components/pages/PersonalInformationPage";
import { TimeTracQAppSettingsWorkspaceManagement } from "@/domains/settings/screen";
import { ChatGPTSettingsView } from "@/domains/settings/screen/ChatGPTSettingsView";
import { GitHubSettingsView } from "@/domains/settings/screen/GitHubSettingsView";
import { MiracleFlowSettingsView } from "@/domains/settings/screen/MiracleFlowSettingsView";
import { OuraSettingsView } from "@/domains/settings/screen/OuraSettingsView";
import { SupabaseSettingsView } from "@/domains/settings/screen/SupabaseSettingsView";
import { TimeTracQSettingsView } from "@/domains/settings/screen/TimeTracQSettingsView";
import { WithingsSettingsView } from "@/domains/settings/screen/WithingsSettingsView";
import { YazioSettingsView } from "@/domains/settings/screen/YazioSettingsView";
import { SettingsApp } from "@/domains/settings/SettingsApp";
import { SwitchBotTokenSettings } from "@/domains/settings/switchbot/SwitchBotTokenSetting";
import { TuyaSettings } from "@/domains/settings/tuya/TuyaSettings";
import { SmartHomeAppWithCards } from "@/domains/smarthome";
import { SwitchBotApp } from "@/domains/smarthome/switchbot/SwitchBotApp";
import { TuyaApp } from "@/domains/smarthome/tuya/TuyaApp";
import {
  AdminPanelApp,
  NexusApp,
  InvestmentsApp,
  TradingApp,
  ResponsumOSApp,
  RealEstateApp,
  OthersApp,
} from "@/domains/workspaces";

export const router = createBrowserRouter([
  {
    children: [
      /* ---------------- Dashboard ---------------- */
      {
        element: <DashboardApp />,
        handle: { label: "Dashboard" },
        path: "dashboard",
      },

      /* -------------- Workspace Roots ------------ */
      {
        element: <AdminPanelApp />,
        handle: { label: "Admin Panel" },
        path: "admin",
      },
      { element: <NexusApp />, handle: { label: "Nexus" }, path: "nexus" },
      {
        children: [
          {
            element: <ParqetApp />,
            handle: { label: "Parqet" },
            path: "parqet",
          },
        ],
        element: <InvestmentsApp />,
        handle: { label: "Investments" },
        path: "investments",
      },
      {
        element: <TradingApp />,
        handle: { label: "Trading" },
        path: "trading",
      },
      {
        element: <ResponsumOSApp />,
        handle: { label: "ResponsumOS" },
        path: "responsumos",
      },
      {
        element: <RealEstateApp />,
        handle: { label: "Real Estate" },
        path: "real-estate",
      },
      {
        element: <SmartHomeAppWithCards />,
        handle: { label: "Smart Home" },
        path: "smarthome",
      },
      {
        element: <SwitchBotApp />,
        handle: { label: "SwitchBot" },
        path: "smarthome/switchbot",
      },
      {
        element: <TuyaApp />,
        handle: { label: "Tuya" },
        path: "smarthome/tuya",
      },
      { element: <OthersApp />, handle: { label: "Others" }, path: "others" },

      /* ---------- Container-Knoten (reine Outlets) ---------- */
      {
        element: <Outlet />,
        handle: { label: "Global Tools" },
        path: "global-tools",
      },
      {
        element: <Outlet />,
        handle: { label: "Smart Home" },
        path: "smarthome",
      },
      {
        element: <HealthApp />,
        handle: { label: "Health" },
        path: "nexus/health",
      },
      {
        children: [
          { element: <OuraDashboardPage />, index: true }, // Dashboard Tab
          { element: <OuraDashboardPage />, path: "dashboard" }, // Dashboard Tab
          {
            element: <ReadinessDetail />,
            handle: { label: "Readiness" },
            path: "readiness",
          },
          {
            element: <SleepDetail />,
            handle: { label: "Sleep" },
            path: "sleep",
          },
          {
            element: <ActivityDetail />,
            handle: { label: "Activity" },
            path: "activity",
          },
          {
            element: <StressDetail />,
            handle: { label: "Stress" },
            path: "stress",
          },
        ],
        element: <OuraApp />,
        handle: { label: "Oura" },
        path: "nexus/health/oura",
      },
      {
        children: [
          { element: <WithingsOverviewPage />, index: true }, // Default Tab
          {
            element: <WithingsOverviewPage />,
            handle: { label: "Übersicht" },
            path: "overview",
          },
          {
            element: <WithingsBodyCompositionPage />,
            handle: { label: "Körperzusammensetzung" },
            path: "body-composition",
          },
          {
            element: <WithingsTrendsPage />,
            handle: { label: "Trends" },
            path: "trends",
          },
          {
            element: <WithingsSyncPage />,
            handle: { label: "Sync" },
            path: "sync",
          },
          {
            element: <WithingsTokenPage />,
            handle: { label: "Tokens" },
            path: "tokens",
          },
        ],
        element: <WithingsApp />,
        handle: { label: "Withings" },
        path: "nexus/health/withings",
      },
      {
        children: [
          { element: <YazioDashboardPage />, index: true }, // Default Tab
          {
            element: <YazioDashboardPage />,
            handle: { label: "Dashboard" },
            path: "dashboard",
          },
          {
            element: <YazioGoalsPage />,
            handle: { label: "Goals" },
            path: "goals",
          },
          {
            element: <YazioMealsPage />,
            handle: { label: "Meals" },
            path: "meals",
          },
          {
            element: <YazioSyncPage />,
            handle: { label: "Sync" },
            path: "sync",
          },
        ],
        element: <YazioApp />,
        handle: { label: "Yazio" },
        path: "nexus/health/yazio",
      },
      {
        element: <PersonalDevelopmentApp />,
        handle: { label: "Personal Development" },
        path: "nexus/personal-development",
      },
      {
        children: [
          {
            element: <MiracleFlowDashboardTab />,
            handle: { label: "Dashboard" },
            index: true,
          },
          {
            element: <ModernMiracleFlowMorningSessionTab />,
            handle: { label: "Morning Session" },
            path: "morning-session",
          },
          {
            element: <ModernMiracleFlowEveningSessionTab />,
            handle: { label: "Evening Session" },
            path: "evening-session",
          },
          {
            element: <MiracleFlowSessionHistoryTab />,
            handle: { label: "Session History" },
            path: "session-history",
          },
          {
            element: <MiracleFlowAffirmationSettingsTab />,
            handle: { label: "Affirmation Settings" },
            path: "affirmation-settings",
          },
          {
            element: <MiracleFlowStatsOverviewTab />,
            handle: { label: "Statistics" },
            path: "stats-overview",
          },
        ],
        element: <MiracleFlowApp />,
        handle: { label: "MiracleFlow" },
        path: "nexus/personal-development/miracle-flow",
      },

      /* ---------------- Blatt-Seiten ---------------- */
      {
        children: [
          {
            element: <TrackingPage />,
            handle: { label: "Tracking" },
            index: true,
          },
          { element: <TasksPage />, handle: { label: "Tasks" }, path: "tasks" },
          {
            element: <DailyPlanPage />,
            handle: { label: "Daily Plan" },
            path: "daily-plan",
          },
          {
            element: <ShortfallsPage />,
            handle: { label: "Shortfalls" },
            path: "shortfalls",
          },
          {
            element: <OverviewPage />,
            handle: { label: "Overview" },
            path: "overview",
          },
          {
            element: <AggregatedViewPage />,
            handle: { label: "Analytics" },
            path: "analytics",
          },
          {
            element: <BuzzerPage />,
            handle: { label: "Buzzer" },
            path: "buzzer",
          },
        ],
        element: <TimeTracQApp />,
        handle: { label: "TimeTracQ" },
        path: "timetracq",
      },
      {
        children: [
          {
            element: <OverviewScreen />,
            handle: { label: "Overview" },
            index: true,
          },
          {
            element: <TimelineScreen />,
            handle: { label: "Timeline" },
            path: "timeline",
          },
          {
            element: <AnalyticsScreen />,
            handle: { label: "Analytics" },
            path: "analytics",
          },
          {
            element: <MatrixScreen />,
            handle: { label: "Priority Matrix" },
            path: "matrix",
          },
          {
            element: <KanbanScreen />,
            handle: { label: "Kanban Board" },
            path: "kanban",
          },
        ],
        element: <RoadmapApp />,
        handle: { label: "Roadmap" },
        path: "roadmap",
      },
      {
        element: <NotificationsApp />,
        handle: { label: "Notifications" },
        path: "admin/notifications",
      },
      {
        element: <RemindersApp />,
        handle: { label: "Reminders" },
        path: "reminders",
      },
      {
        children: [
          {
            element: <MiracleFlowDashboardTab />,
            handle: { label: "Dashboard" },
            index: true,
          },
          {
            element: <ModernMiracleFlowMorningSessionTab />,
            handle: { label: "Morning Session" },
            path: "morning-session",
          },
          {
            element: <ModernMiracleFlowEveningSessionTab />,
            handle: { label: "Evening Session" },
            path: "evening-session",
          },
          {
            element: <MiracleFlowSessionHistoryTab />,
            handle: { label: "Session History" },
            path: "session-history",
          },
          {
            element: <MiracleFlowAffirmationSettingsTab />,
            handle: { label: "Affirmation Settings" },
            path: "affirmation-settings",
          },
          {
            element: <MiracleFlowStatsOverviewTab />,
            handle: { label: "Statistics" },
            path: "stats-overview",
          },
        ],
        element: <MiracleFlowApp />,
        handle: { label: "MiracleFlow" },
        path: "miracle-flow",
      },
      { element: <LLMApp />, handle: { label: "LLM Services" }, path: "llm" },
      {
        element: <ChatGPTApp />,
        handle: { label: "ChatGPT" },
        path: "llm/chatgpt",
      },
      {
        element: <OllamaApp />,
        handle: { label: "Ollama" },
        path: "llm/ollama",
      },

      /* -------------- Weitere Einzelseiten -------------- */
      {
        element: <AggregatedViewPage />,
        handle: { label: "Aggregated View" },
        path: "aggregated",
      },
      {
        element: <SpotifyPage />,
        handle: { label: "Spotify" },
        path: "spotify",
      },
      {
        element: <CalendarPage />,
        handle: { label: "Calendar" },
        path: "calendar",
      },

      /* ---------------- Settings mit Unternavigation ---------------- */
      {
        children: [
          {
            element: <PersonalInformationPage />,
            handle: { label: "Personal Information" },
            index: true,
          },
          {
            element: <PersonalInformationPage />,
            handle: { label: "Personal Information" },
            path: "personal",
          },
          {
            element: <SupabaseSettingsView />,
            handle: { label: "Supabase" },
            path: "supabase",
          },
          {
            element: <GitHubSettingsView />,
            handle: { label: "GitHub" },
            path: "github",
          },
          {
            element: <ChatGPTSettingsView />,
            handle: { label: "ChatGPT" },
            path: "chatgpt",
          },
          {
            element: <OuraSettingsView />,
            handle: { label: "Oura" },
            path: "oura",
          },
          {
            element: <ParqetSettingsView />,
            handle: { label: "Parqet" },
            path: "parqet",
          },
          {
            element: <WithingsSettingsView />,
            handle: { label: "Withings" },
            path: "withings",
          },
          {
            element: <YazioSettingsView />,
            handle: { label: "Yazio" },
            path: "yazio",
          },
          {
            element: <MiracleFlowSettingsView />,
            handle: { label: "MiracleFlow" },
            path: "miracle-flow",
          },
          {
            element: <TimeTracQSettingsView />,
            handle: { label: "TimeTracQ" },
            path: "timetracq",
          },
          {
            element: <TimeTracQAppSettingsWorkspaceManagement />,
            handle: { label: "TimeTracQ Workspaces" },
            path: "timetracq/workspaces",
          },
          {
            element: <SwitchBotTokenSettings />,
            handle: { label: "SwitchBot" },
            path: "switchbot",
          },
          {
            element: <TuyaSettings />,
            handle: { label: "Tuya" },
            path: "tuya",
          },
        ],
        element: <SettingsApp />,
        handle: { label: "Settings" },
        path: "settings",
      },
    ],
    element: <AppLayout />, // Zentrale Layout-Komponente mit Dark/Blue Mode
    errorElement: <ErrorPage />,
    path: "/",
  },
]);
